import { useCallback, useEffect, useState } from 'react'
import { Plus } from 'lucide-react'
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal'
import Toast from '../../../../components/Toast/Toast'
import { useToast } from '../../../../hooks/useToast'
import { patientService } from '../../../../services/patientApi'
import MedicationPrn from './MedicationPrn'
import MedicationPrescriptionModal from './MedicationPrescriptionModal'
import MedicationPrescriptions from './MedicationPrescriptions'
import MedicationTimeline from './MedicationTimeline'
import { todayInputValue } from './medicationFormat'

function MedicationsTab({ patientId, archived, canPrescribe = false, canAdminister = false, allergies, prescribeTick, onChanged }) {
  const [date, setDate] = useState(todayInputValue)
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [open, setOpen] = useState(false)
  const [editing, setEditing] = useState(null)
  const [suspending, setSuspending] = useState(null)
  const [suspendReason, setSuspendReason] = useState('')
  const { toast, showToast, hideToast } = useToast()

  const load = useCallback(async () => {
    setError('')
    try {
      const response = await patientService.medicationDay(patientId, date)
      setData(response.data)
    } catch (err) {
      setError(err.response?.data?.message || 'Não foi possível carregar os medicamentos')
    }
  }, [patientId, date])

  useEffect(() => {
    setData(null)
    load()
  }, [load])

  useEffect(() => {
    if (!prescribeTick || !canPrescribe) return
    setEditing(null)
    setOpen(true)
  }, [prescribeTick, canPrescribe])

  const refresh = async (message) => {
    await load()
    if (onChanged) onChanged()
    if (message) showToast(message, 'success')
  }

  const run = async (action, message) => {
    setError('')
    try {
      await action()
      await refresh(message)
    } catch (err) {
      const text = err.response?.data?.message || 'Não foi possível atualizar a medicação'
      setError(text)
      showToast(text, 'error')
    }
  }

  const closeSuspend = () => {
    setSuspending(null)
    setSuspendReason('')
  }

  const confirmSuspend = () => {
    const item = suspending
    const reason = suspendReason.trim()
    closeSuspend()
    if (!item) return
    run(() => patientService.suspendPrescription(patientId, item.id, reason), 'Prescrição suspensa.')
  }

  return (
    <div className="med-panel">
      <Toast message={toast.message} type={toast.type} show={toast.show} onClose={hideToast} />
      {canPrescribe && (
        <div className="med-toolbar">
          <button type="button" className="patients-btn patients-btn--primary" onClick={() => { setEditing(null); setOpen(true) }}>
            <Plus size={14} /> Prescrever
          </button>
        </div>
      )}
      {error && <p className="patients-alert-strip">{error}</p>}
      <div className="med-board">
        <MedicationTimeline
          date={date}
          onDateChange={setDate}
          doses={data?.timeline || []}
          canEdit={canAdminister && Boolean(data)}
          onAdminister={(dose) => run(() => patientService.administerSchedule(patientId, dose.id), 'Dose administrada.')}
          onRefuse={(dose, reason) => run(() => patientService.refuseSchedule(patientId, dose.id, reason), 'Dose recusada.')}
        />
        <div className="med-side">
          {!data && <p className="patients-muted">Carregando medicamentos…</p>}
          <MedicationPrescriptions
            active={data?.active || []}
            suspended={data?.suspended || []}
            canEdit={canPrescribe && Boolean(data)}
            onEdit={(item) => { setEditing(item); setOpen(true) }}
            onSuspend={(item) => { setSuspendReason(''); setSuspending(item) }}
            onReload={(item, quantity) => run(() => patientService.reloadPrescriptionStock(patientId, item.id, quantity), 'Estoque atualizado.')}
          />
          <MedicationPrn
            items={data?.prn || []}
            canEdit={canAdminister && Boolean(data)}
            onAdminister={(item, reason) => run(() => patientService.administerPrn(patientId, item.id, reason), 'Dose registrada.')}
          />
        </div>
      </div>
      <ConfirmModal
        isOpen={Boolean(suspending)}
        onClose={closeSuspend}
        onConfirm={confirmSuspend}
        title="Suspender prescrição"
        type="warning"
        confirmText="Suspender"
        message={(
          <div className="med-suspend-copy">
            <p>
              Suspender <strong>{suspending?.name}</strong>? As doses pendentes deixam de aparecer na linha do tempo.
            </p>
            <label className="med-suspend-field">
              Motivo (opcional)
              <textarea
                value={suspendReason}
                onChange={(event) => setSuspendReason(event.target.value)}
                maxLength={200}
                rows={3}
                placeholder="Ex.: alta, troca de esquema, reação"
              />
            </label>
          </div>
        )}
      />
      <MedicationPrescriptionModal
        isOpen={open}
        patientId={patientId}
        prescription={editing}
        allergies={allergies}
        onClose={() => setOpen(false)}
        onSaved={() => refresh('Prescrição salva.')}
      />
      {archived && <p className="patients-muted">Paciente arquivado. O aprazamento fica somente para leitura.</p>}
    </div>
  )
}

export default MedicationsTab
